import React, { useEffect } from "react";
import lodash from "lodash";
import { useForm } from "react-hook-form";
import { v4 as uuidv4 } from "uuid";

// COMPONENTS
import KogInputTextControlledNew from "@/main/components/shared/inputText/inputTextControlledNew";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";

// UTILS
import { IDataDeliveryLogistic, IStatesDataDeliveryLogistic } from "../types";
import { getTranslateByKey } from "@/main/services/utils";
import * as S from "./styles";
import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";

// REDUCERS
import { getDeliveryTypes, useDeliveryTypeReducer } from "@/main/store/deliveryType";
import { useDispatch } from "react-redux";
import { KeyValue } from "@/main/models/common";

const INITIAL_FORM_STATE = {
	initialZipCode: "" as string,
	finalZipCode: "" as string,
	daysDelivery: "" as string,
	deliveryType: null as KeyValue | null,
};

type IAddressInfoForm = typeof INITIAL_FORM_STATE;

interface IHeaderFormLogisticProps extends IStatesDataDeliveryLogistic {
	setIdsErrorDataDeliverLogistic: React.Dispatch<React.SetStateAction<string[]>>;
}

function HeaderFormLogistic({
	dataDeliverLogistic,
	setDataDeliverLogistic,
	setIdsErrorDataDeliverLogistic,
}: IHeaderFormLogisticProps) {
	const {
		control,
		formState: { errors },
		setFocus,
		handleSubmit,
		setError,
		reset: resetForm,
	} = useForm<IAddressInfoForm>({
		defaultValues: INITIAL_FORM_STATE,
	});

	const { deliveryTypes: deliveryTypeReducer } = useDeliveryTypeReducer();
	const dispatch = useDispatch();

	function setLineError(id: string) {
		setIdsErrorDataDeliverLogistic((prev) => (prev.includes(id) ? prev : [...prev, id]));
	}
	function clearLineError() {
		setIdsErrorDataDeliverLogistic([]);
	}
	interface IErrorValidation {
		idLineError?: string;
		nameInputError: keyof IAddressInfoForm;
		message: string;
	}
	function errorValidation({ idLineError, nameInputError, message }: IErrorValidation) {
		if (idLineError) {
			setLineError(idLineError);
		}

		setError(
			nameInputError,
			{
				type: idLineError ? "manualLine" : "manual",
				message: message,
			},
			{ shouldFocus: true },
		);
	}
	function handleSubmitLogisticCompany(data: IAddressInfoForm) {
		const newDataDeliverLogistic: IDataDeliveryLogistic = {
			id: uuidv4(),
			initialZipCode: data.initialZipCode,
			finalZipCode: data.finalZipCode,
			daysDelivery: data.daysDelivery,
			concatString: `${data.initialZipCode}${data.finalZipCode}${data.daysDelivery}`,
			deliveryType: String(data.deliveryType?.value),
			idDeliveryType: Number(data.deliveryType?.key),
		};
		const newInitialZipCode = Number(newDataDeliverLogistic.initialZipCode);
		const newFinalZipCode = Number(newDataDeliverLogistic.finalZipCode);
		const haveLowerInitialZipCode = newInitialZipCode <= newFinalZipCode;
		clearLineError();
		if (!haveLowerInitialZipCode) {
			errorValidation({
				nameInputError: "initialZipCode",
				message: getTranslateByKey("information.initialLessEndZipcode", true),
			});
			return;
		}

		const isRange = dataDeliverLogistic.map((item) => {
			const isRangedInitialZipCode =
				lodash.inRange(newInitialZipCode, Number(item.initialZipCode), Number(item.finalZipCode)) ||
				newInitialZipCode === Number(item.initialZipCode) ||
				newInitialZipCode === Number(item.finalZipCode);
			const isRangedItemInitialZipCode = lodash.inRange(
				Number(item.initialZipCode),
				newInitialZipCode,
				newFinalZipCode,
			);
			const isRangedFinalZipCode =
				lodash.inRange(newFinalZipCode, Number(item.initialZipCode), Number(item.finalZipCode)) ||
				newFinalZipCode === Number(item.initialZipCode) ||
				newFinalZipCode === Number(item.finalZipCode);
			const isRangedItemFinalZipCode = lodash.inRange(Number(item.finalZipCode), newInitialZipCode, newFinalZipCode);

			if (isRangedInitialZipCode) {
				errorValidation({
					idLineError: item.id,
					nameInputError: "initialZipCode",
					message: getTranslateByKey("information.initialZipCodeRegistered", true),
				});
			}
			if (isRangedItemInitialZipCode) {
				errorValidation({
					idLineError: item.id,
					nameInputError: "finalZipCode",
					message: getTranslateByKey("information.existsRange", true),
				});
			}

			if (isRangedFinalZipCode) {
				errorValidation({
					idLineError: item.id,
					nameInputError: "finalZipCode",
					message: getTranslateByKey("information.finalZipCodeRegistered", true),
				});
			}

			if (isRangedItemFinalZipCode) {
				errorValidation({
					idLineError: item.id,
					nameInputError: "initialZipCode",
					message: getTranslateByKey("information.existsRange", true),
				});
			}

			return isRangedInitialZipCode || isRangedFinalZipCode || isRangedItemInitialZipCode || isRangedItemFinalZipCode;
		});
		const haveErrorRage = isRange.some((item) => item);
		if (haveErrorRage) {
			return;
		}
		resetForm();
		setDataDeliverLogistic((prev) => [...prev, newDataDeliverLogistic]);
		setFocus("initialZipCode");
	}

	useEffect(() => {
		const errorsZipCode = Object.keys(errors).filter((item) => item.includes("ZipCode"));
		const haveErrorZipCode = errorsZipCode.some((item) => errors[item as keyof typeof errors]?.type === "manualLine");
		if (!haveErrorZipCode) {
			clearLineError();
		}
	}, [JSON.stringify(errors)]);

	useEffect(() => {
		if (deliveryTypeReducer.data === null) dispatch(getDeliveryTypes());
	}, []);

	// Contants Layout
	const MAX_LENGTH_ZIP_CODE = 5;

	return (
		<form onSubmit={handleSubmit(handleSubmitLogisticCompany)} noValidate>
			<S.Container>
				<KogAutoCompleteControlled
					nameCustom="deliveryType"
					control={control}
					isDefaultValueUnique
					id="id-registerSupplier-dropdown-delivery-type"
					label={getTranslateByKey("information.deliveryType")}
					rulesCustom={{ required: true }}
					options={deliveryTypeReducer.data}
					isLoading={deliveryTypeReducer.isLoading}
					onChangeCustom={() => {}}
				/>
				<KogInputTextControlledNew
					hasOnlyNumberInteger
					required
					id="id-registerSupplier-txt-initialZipCode"
					name="initialZipCode"
					label={getTranslateByKey("information.initialZipCode")}
					control={control}
					inputProps={{ maxLength: MAX_LENGTH_ZIP_CODE }}
				/>
				<KogInputTextControlledNew
					hasOnlyNumberInteger
					required
					id="id-registerSupplier-txt-finalZipCode"
					name="finalZipCode"
					label={getTranslateByKey("information.finalZipCode")}
					control={control}
					inputProps={{ maxLength: MAX_LENGTH_ZIP_CODE }}
				/>
				<KogInputTextControlledNew
					hasOnlyNumberInteger
					required
					id="id-registerSupplier-txt-daysDelivery"
					name="daysDelivery"
					label={getTranslateByKey("information.daysDeliveryPart")}
					control={control}
					inputProps={{ maxLength: 3 }}
				/>
				<SecondaryButton id="id-registerSupplier-btn-save" type="submit">
					{getTranslateByKey("information.add")}
				</SecondaryButton>
			</S.Container>
		</form>
	);
}

export default HeaderFormLogistic;
